import React, { useState, useEffect, useRef, useCallback, useMemo, memo } from "react"
import { Loader2 } from "lucide-react"
import { MarkdownEditor } from "./markdown-editor"
import { ChangeDetectionBanner } from "./change-detection-banner"
import { useTheme } from "@/hooks/use-theme"
import { usePersistedState } from "../../hooks/use-persisted-state"
import { useKeyboardShortcuts } from "../../hooks/use-keyboard-shortcuts"
import { testIds } from "@/lib/test-ids"
import { StorageKeys, StorageSchemas } from "../../lib/storage"
import {
  assembleSourceMarkdownText,
  parseMarkdownWithFrontmatter,
  splitApiDocumentBodyAndFrontmatter,
} from "../../lib/markdown-frontmatter"
import type { ChangedLines, Document } from "../../lib/api"
import { getRenderer, resolveDocumentRendererKind } from "@/lib/document-renderer-registry"
import { IS_LOCAL_API } from "../../lib/api"
import type { ChangedLinesState } from "@/lib/codemirror-extensions/change-gutter"
import type { DocumentContentProps } from "../layout/document-viewer.types"
import { isMarkdownProductBoardFilePath, isPathKeyedUnresolvedDocumentId, normalizeChangedLines } from "../layout/document-viewer.utils"
import { DocumentContentHtmlPanel } from "./document-content-html-panel"
import { CsvDocumentViewer } from "./csv-document-viewer"
import { isCsvDocument } from "./document-list.utils"
import { DocumentContentSourceFrontmatterBar } from "./document-content-source-frontmatter-bar"
import { DocumentContentStatusBar } from "./document-content-status-bar"
import { LIST_GESTURE_TIPS_PRODUCT_BOARD } from "@/lib/list-gesture-tips"
import { fetchWithRetry } from "../layout/resizable-layout.utils"
import { fetchWithAuth } from "@/lib/api"
import {
  isBasecampIntegrationTabId,
  resolveBasecampTabFromShorthandPath,
} from "@/lib/basecamp-tab-routing"
import { resolveStoragePathForFetch } from "@/lib/app-routes"
import { useWorkspace } from "@/providers/workspace-provider"
import { isChatMessageEditOpen } from "@/lib/chat-message-edit-dismiss"

/** Ignore file-mtime / poll diffs shortly after webapp auto-save (file watcher round-trip). */
const WEBAPP_WRITE_GRACE_MS = 10_000

function contentFingerprint(content: string | undefined | null): string {
  if (!content) return ""
  return content.substring(0, 100) + content.length
}

function collectDocumentMatchKeys(...values: (string | null | undefined)[]): Set<string> {
  const keys = new Set<string>()
  for (const value of values) {
    if (typeof value !== "string") continue
    const trimmed = value.trim()
    if (!trimmed) continue
    keys.add(trimmed)
    keys.add(trimmed.replace(/\.md$/i, ""))
  }
  return keys
}

function documentUpdateMatchesTab(
  documentId: string,
  updatedDocId: string | undefined,
  updatedPath: string | undefined,
  extraIds: (string | null | undefined)[]
): boolean {
  const currentKeys = collectDocumentMatchKeys(documentId, ...extraIds)
  const updateKeys = collectDocumentMatchKeys(updatedDocId, updatedPath)
  for (const key of updateKeys) {
    if (currentKeys.has(key)) return true
  }
  return false
}

function bodyAndFrontmatterFromApiDoc(doc: {
  type?: string | null
  filePath?: string | null
  file_path?: string | null
  content?: string | null
  frontmatter?: Record<string, unknown> | null
}): { body: string; frontmatter: Record<string, unknown> } {
  const raw = doc.content || ""
  if (isCsvDocument({ type: doc.type, filePath: doc.filePath || doc.file_path })) {
    return { body: raw, frontmatter: (doc.frontmatter || {}) as Record<string, unknown> }
  }
  return splitApiDocumentBodyAndFrontmatter(raw, doc.frontmatter)
}
import { devLog } from "@/lib/dev-log"
import { navDebug } from "@/lib/nav-debug"
import { wasDocumentRecentlyDeleted } from "@/lib/document-delete-utils"
import { consumeDocumentEditOnOpen } from "@/lib/pending-document-edit-focus"
import {
  captureDocumentScrollRatio,
  restoreDocumentScrollRatio,
} from "@/lib/document-scroll-restore"

const DocumentContentInner = ({
  documentId,
  documentFilePathFallback = null,
  documentTabActive = true,
  onAIDocumentSave,
  onAIDocumentCancel,
  onSendToChat,
  onInternalLinkClick,
  onManualSave,
  onManualSaveComplete,
  saving,
  isDeleted,
  onFrontmatterChange,
  onEditorModeChange,
  onChangeBannerVisibleChange,
  onEditorFocusChange,
  sidebarFocusCallback,
  onDocumentMetaSync,
  driveSyncActivity,
  driveSyncLabel,
  driveGdocId,
  driveOrganizationId,
  driveLastSynced,
  driveLastPushed,
  driveLastPulled,
  driveKeepSynced,
}: DocumentContentProps) => {
  const { resolvedTheme } = useTheme()
  const { workspaces } = useWorkspace()
  const workspacesRoute = useMemo(
    () => workspaces.map((w) => ({ id: w.id, slug: w.slug, contentRoot: w.contentRoot })),
    [workspaces]
  )
  const [content, setContent] = useState("")
  const [loading, setLoading] = useState(true)
  const [localSaving, setLocalSaving] = useState(false)
  const [lastSaved, setLastSaved] = useState<Date | null>(null)
  const [lastFileSave, setLastFileSave] = useState<Date | null>(null)
  const [fileModifiedAt, setFileModifiedAt] = useState<string | null>(null) // Track file modification time for editor key
  const [editorRefreshKey, setEditorRefreshKey] = useState(0) // Increment on explicit refresh so editor remounts and shows new content
  const [wysiwygEditorMounted, setWysiwygEditorMounted] = useState(false)
  const [wysiwygAutoFocus, setWysiwygAutoFocus] = useState(false)
  const wysiwygFlushRef = useRef<(() => string) | null>(null)
  const contentRootRef = useRef<HTMLDivElement>(null)
  const pendingScrollRestoreRef = useRef<number | null>(null)

  const handleSetWysiwygEditorMounted = useCallback((mounted: boolean) => {
    if (!mounted && wysiwygFlushRef.current) {
      wysiwygFlushRef.current()
    }
    setWysiwygEditorMounted(mounted)
  }, [])
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false)
  const [_error, setError] = useState<string | null>(null)
  const [saveTimeoutRef, setSaveTimeoutRef] = useState<ReturnType<typeof setTimeout> | null>(null)
  const [localIsDeleted, setLocalIsDeleted] = useState(false)
  const [deletedMessage, setDeletedMessage] = useState<string | null>(null)
  const [frontmatter, setFrontmatter] = useState<Record<string, any> | null>(null)
  const [viewMode, setViewMode] = usePersistedState<'wysiwyg' | 'source'>({
    key: StorageKeys.EDITOR_MODE,
    defaultValue: 'wysiwyg',
    schema: StorageSchemas.editorMode,
  })
  const [pageWidthMode, setPageWidthMode] = usePersistedState<'default' | 'full'>({
    key: StorageKeys.PAGE_WIDTH_MODE,
    defaultValue: 'default',
    schema: StorageSchemas.pageWidthMode,
  })
  const [sourceFrontmatterPrefs, setSourceFrontmatterPrefs] = usePersistedState<
    Record<string, boolean>
  >({
    key: StorageKeys.SOURCE_FRONTMATTER_PREFS,
    defaultValue: {},
    schema: StorageSchemas.sourceFrontmatterPrefs,
  })
  const [showFrontmatterInSource, setShowFrontmatterInSource] = useState(false)
  const [sourceDraft, setSourceDraft] = useState<string | null>(null)
  const [documentChangedLines, setDocumentChangedLines] = useState<ChangedLines | null>(null)
  const [baselineContent, setBaselineContent] = useState<string | null>(null)
  // State for current document metadata (for read-only check)
  const [currentDocument, setCurrentDocument] = useState<any>(null)
  // State for editor focus (for visual highlight)
  const [isEditorFocused, setIsEditorFocused] = useState(false)

  // Keep the parent tab's title/filePath in sync with the loaded document whenever either
  // changes - covers renames from any source (AI tool call, another tab/window, external file
  // change), not just the manual UI rename flow which already updates the tab directly. Without
  // this, the header/tab title only reflects a rename after a full page reload (see incident
  // docs/product/incidents/2026-07-08-document-rename-editor-desync.md).
  useEffect(() => {
    const apply = (top: number) => {
      const root = contentRootRef.current
      if (!root) return
      const scroller =
        root.querySelector('[data-scroll-container="true"]') ??
        root.querySelector('.cm-scroller')
      if (scroller instanceof HTMLElement) scroller.scrollTop = top
    }
    const onRestore = (event: Event) => {
      const top = (event as CustomEvent<{ scrollTop?: number }>).detail?.scrollTop
      if (typeof top !== 'number') return
      apply(top)
      window.requestAnimationFrame(() => apply(top))
      window.setTimeout(() => apply(top), 50)
      window.setTimeout(() => apply(top), 300)
    }
    window.addEventListener('review-scroll-restore', onRestore)
    return () => window.removeEventListener('review-scroll-restore', onRestore)
  }, [])

  useEffect(() => {
    if (!documentId || !currentDocument) return
    const title = typeof currentDocument.title === 'string' ? currentDocument.title : undefined
    const filePath = (currentDocument.filePath ?? currentDocument.file_path) as string | undefined
    if (title === undefined && filePath === undefined) return
    onDocumentMetaSync?.(documentId, { title, filePath: filePath ?? null })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentId, currentDocument?.title, currentDocument?.filePath, currentDocument?.file_path])

  const structuralKind = useMemo(() => {
    const kind = String(frontmatter?.kind || "").trim()
    if (kind) {
      return resolveDocumentRendererKind({
        kind,
        filePath:
          currentDocument?.filePath ||
          currentDocument?.file_path ||
          documentFilePathFallback,
      })
    }
    const fmType = String(frontmatter?.type || "").trim()
    const path = String(
      currentDocument?.filePath || currentDocument?.file_path || documentFilePathFallback || "",
    )
    const inferredType =
      fmType === "product-board" || (path && isMarkdownProductBoardFilePath(path))
        ? "product-board"
        : fmType || String(currentDocument?.type || "note")
    return resolveDocumentRendererKind({ type: inferredType, filePath: path })
  }, [
    frontmatter?.kind,
    frontmatter?.type,
    currentDocument?.type,
    currentDocument?.filePath,
    currentDocument?.file_path,
    documentFilePathFallback,
  ])

  const CustomRenderer = useMemo(
    () => getRenderer(structuralKind),
    [structuralKind]
  )

  const defaultSourceShowsFrontmatter = useCallback((kind: string) => {
    return kind === "flow" || kind === "skill" || kind === "widget" || kind === "quiz" || kind === "product-board" || kind === "session_image"
  }, [])

  useEffect(() => {
    const pref = sourceFrontmatterPrefs[structuralKind]
    setShowFrontmatterInSource(
      pref !== undefined ? pref : defaultSourceShowsFrontmatter(structuralKind)
    )
  }, [
    documentId,
    structuralKind,
    sourceFrontmatterPrefs,
    defaultSourceShowsFrontmatter,
  ])

  useEffect(() => {
    onEditorModeChange?.(viewMode)
  }, [viewMode, onEditorModeChange])

  const serializedSourceWithFrontmatter = useMemo(
    () => assembleSourceMarkdownText(content, frontmatter),
    [content, frontmatter]
  )

  /** Source view always edits the on-disk file (body + frontmatter); visibility of YAML is a fold, not a different string. */
  const markdownInitialContent = useMemo(() => {
    if (viewMode !== "source") return content
    return sourceDraft ?? serializedSourceWithFrontmatter
  }, [viewMode, content, sourceDraft, serializedSourceWithFrontmatter])

  useEffect(() => {
    if (viewMode !== "wysiwyg" || sourceDraft === null) return
    const parsed = parseMarkdownWithFrontmatter(sourceDraft)
    if (!parsed.parseFailed) {
      setContent(parsed.body)
      setFrontmatter(parsed.frontmatter as Record<string, any>)
    }
    setSourceDraft(null)
  }, [viewMode])

  // ESC key handler to exit editor focus, return to preview, or focus sidebar
  useKeyboardShortcuts([
    {
      key: "Escape",
      handler: () => {
        const active = document.activeElement
        // Message edit handles Escape itself, including after focus left the chat.
        // This shortcut runs on window capture and would otherwise blur the document first.
        if (isChatMessageEditOpen()) return false
        if (
          active instanceof Element &&
          active.closest(`[data-testid="${testIds.chatPane.container}"]`)
        ) {
          return false
        }
        if (!isEditorFocused) return false
        const exitingToPreview = viewMode === "wysiwyg" && wysiwygEditorMounted
        setIsEditorFocused(false)
        onEditorFocusChange?.(false)
        if (exitingToPreview) {
          wysiwygFlushRef.current?.()
          handleSetWysiwygEditorMounted(false)
        }
        // Blur any active editor
        const activeElement = document.activeElement as HTMLElement
        if (activeElement && (activeElement.tagName === 'TEXTAREA' || activeElement.contentEditable === 'true')) {
          activeElement.blur()
        }
        if (!exitingToPreview) {
          // Focus sidebar when leaving source or other focused editor states
          if (sidebarFocusCallback) {
            sidebarFocusCallback()
          } else {
            const sidebarElement = document.querySelector('[data-sidebar-focusable]') as HTMLElement
            if (sidebarElement) {
              sidebarElement.focus()
            }
          }
        }
      },
      description: "Exit editor focus or return to preview",
    },
  ])


  // Sticky file_sync markers often predate a later user/extension PUT (changed_at stays
  // old while documents.updated_at moves). Hide banner + gutters when markers are already
  // stale relative to the last write - same class of noise as VSIX false conflicts.
  const autoMarkersAreCurrent = (() => {
    if (!documentChangedLines || documentChangedLines.change_status !== 'auto') return false
    const markerAt = documentChangedLines.changed_at
      ? Date.parse(documentChangedLines.changed_at)
      : NaN
    const updatedAtRaw =
      currentDocument?.updated_at ??
      (currentDocument as { updatedAt?: string } | null)?.updatedAt ??
      null
    const updatedAt = updatedAtRaw ? Date.parse(updatedAtRaw) : NaN
    if (!Number.isFinite(markerAt) || !Number.isFinite(updatedAt)) return true
    // 2s skew: marker write and row updated_at are not always identical timestamps
    return markerAt + 2000 >= updatedAt
  })()

  // Visual change gutters: only for unreviewed external/auto edits (file sync, Drive).
  // After chat Accept, change_status is 'accepted' - the user already reviewed the diff;
  // keeping red/green gutters made source look like the edit never landed.
  const showChangeMarkers = Boolean(
    documentChangedLines &&
      documentChangedLines.change_status === 'auto' &&
      autoMarkersAreCurrent,
  )

  // Visual-mode banner is for unreviewed external/auto edits only.
  const changeBannerVisible =
    viewMode === "wysiwyg" &&
    Boolean(
      documentChangedLines &&
        documentChangedLines.change_status === 'auto' &&
        showChangeMarkers,
    )

  useEffect(() => {
    onChangeBannerVisibleChange?.(changeBannerVisible)
    return () => onChangeBannerVisibleChange?.(false)
  }, [changeBannerVisible, onChangeBannerVisibleChange])

  // Store baseline content when changes are first detected
  useEffect(() => {
    if (documentChangedLines &&
        (documentChangedLines.change_status === 'accepted' ||
         documentChangedLines.change_status === 'auto')) {
      // First time seeing changes - store baseline
      setBaselineContent(prev => {
        if (!prev && content) {
          devLog('[DocumentContent] Stored baseline content for diff')
          return content
        }
        return prev
      })
    } else if (!documentChangedLines) {
      // No changes - clear baseline
      setBaselineContent(null)
    }
  }, [documentChangedLines?.change_status, content])

  const editorChangedLines = useMemo<ChangedLinesState | null>(() => {
    if (!documentChangedLines) {
      return null
    }
    return {
      added: documentChangedLines.added ?? [],
      removed: documentChangedLines.removed ?? [],
      modified: documentChangedLines.modified ?? [],
    }
  }, [documentChangedLines])

  // Notify parent component when frontmatter changes (pass documentId for stable callback memoization)
  useEffect(() => {
    onFrontmatterChange?.(frontmatter, documentId)
  }, [frontmatter, documentId, onFrontmatterChange])

  // Save on document switch (when documentId changes)
  const contentRef = useRef<string>("")
  const hasUnsavedChangesRef = useRef<boolean>(false)
  const autoSaveRefForSwitch = useRef<((contentToSave: string, options?: { immediate?: boolean }) => Promise<void>) | null>(null)
  const lastContentHashRef = useRef<string | null>(null)
  const lastPolledFileFingerprintRef = useRef<string | null>(null)
  const lastWebappWriteTimeRef = useRef<number | null>(null) // Track when webapp last wrote to file
  const localSavingRef = useRef(false)
  const initialLoadCompleteRef = useRef(false)
  /** Last documentId that finished a successful content load (soft-reload / no flash). */
  const lastLoadedDocumentIdRef = useRef<string | null>(null)
  const prevDocumentIdForEditGateRef = useRef<string | null>(null)
  /** Prefer by-path fetch/poll when tab id is a stale UUID but we know content path. */
  const fetchKeyPathRef = useRef<string | null>(null)
  /** One markTabContentVisible per load cycle (active tab, real documents only). */
  const navTimingReportedForLoadRef = useRef<string | null>(null)

  // Keep refs in sync with state
  useEffect(() => {
    contentRef.current = content
  }, [content])

  useEffect(() => {
    hasUnsavedChangesRef.current = hasUnsavedChanges
  }, [hasUnsavedChanges])

  const sourceDraftRef = useRef<string | null>(null)
  useEffect(() => {
    sourceDraftRef.current = sourceDraft
  }, [sourceDraft])

  useEffect(() => {
    localSavingRef.current = localSaving
  }, [localSaving])

  useEffect(() => {
    // Cleanup function runs before documentId changes
    return () => {
      // Save current document when switching away from it
      if (documentId &&
          documentId !== 'list' &&
          !documentId.startsWith('new-') &&
          hasUnsavedChangesRef.current &&
          autoSaveRefForSwitch.current) {
        // Save the current document's content (stored in ref)
        const contentToSave = contentRef.current
        autoSaveRefForSwitch.current(contentToSave, { immediate: true }).catch(err => {
          console.warn('[Auto-save] Failed to save on document switch:', err)
        })
      }
    }
  }, [documentId])

  // Reset poll baseline only when switching documents (not when tab active toggles).
  useEffect(() => {
    lastPolledFileFingerprintRef.current = null
    lastWebappWriteTimeRef.current = null
    initialLoadCompleteRef.current = false
    lastContentHashRef.current = null
  }, [documentId])

  // Poll for document updates (when file watcher syncs changes from Cursor edits).
  // Only meaningful in local dev — cloud has no filesystem watcher and forceReadFromFile is a no-op.
  useEffect(() => {
    if (!IS_LOCAL_API) return

    if (!documentId || documentId === 'list' || documentId === 'home' || documentId.startsWith('new-')) {
      return
    }

    if (isPathKeyedUnresolvedDocumentId(documentId)) {
      return
    }

    if (documentId.startsWith('workspace-home-')) {
      return
    }

    // Skip polling for integration tabs
    if (documentId && (
        documentId === 'basecamp' ||
        documentId === 'basecamp-triage' ||
        documentId.startsWith('basecamp-project-') ||
        documentId.startsWith('basecamp-todo-') ||
        documentId.startsWith('integration-')
      )) {
      return
    }

    if (!documentTabActive) {
      return
    }

    const pollForUpdates = async () => {
      if (!document.hasFocus() || !initialLoadCompleteRef.current) {
        return
      }

      try {
        let apiUrl: string
        const pathKey = fetchKeyPathRef.current
        if (pathKey && pathKey.includes('/')) {
          apiUrl = `/api/documents/by-path?path=${encodeURIComponent(pathKey)}&forceReadFromFile=true`
        } else if (documentId.includes('/')) {
          apiUrl = `/api/documents/by-path?path=${encodeURIComponent(documentId)}&forceReadFromFile=true`
        } else {
          apiUrl = `/api/documents/${documentId}?forceReadFromFile=true`
        }

        const response = await fetchWithRetry(apiUrl, { retryOn404: false, max404Retries: 0 })
        if (response.status === 403) {
          // Stale path after cross-workspace move; forceReadFromFile on old path is forbidden.
          // Tab sync should update documentId — skip polling until remount.
          return
        }
        if (response.status === 404) {
          // forceReadFromFile 404 often means "file not on disk" not "document deleted"
          // (remote DB + local API). Retry from DB before closing the tab.
          if (apiUrl.includes('forceReadFromFile=true')) {
            let dbUrl: string
            if (pathKey && pathKey.includes('/')) {
              dbUrl = `/api/documents/by-path?path=${encodeURIComponent(pathKey)}`
            } else if (documentId.includes('/')) {
              dbUrl = `/api/documents/by-path?path=${encodeURIComponent(documentId)}`
            } else {
              dbUrl = `/api/documents/${documentId}`
            }
            const dbResponse = await fetchWithRetry(dbUrl, { retryOn404: false, max404Retries: 0 })
            if (dbResponse.ok) {
              const doc = await dbResponse.json()
              const newFingerprint = contentFingerprint(doc.content)
              if (newFingerprint !== lastPolledFileFingerprintRef.current) {
                lastPolledFileFingerprintRef.current = newFingerprint
              }
              const incomingChanged = normalizeChangedLines(doc.changedLines ?? doc.changed_lines)
              setDocumentChangedLines(incomingChanged)
              return
            }
          }
          const pollPath = fetchKeyPathRef.current ?? documentFilePathFallback ?? null
          window.dispatchEvent(new CustomEvent('document-deleted', {
            detail: {
              documentId,
              path: pollPath && pollPath.includes('/') ? pollPath : documentId,
              filePath: pollPath && pollPath.includes('/') ? pollPath : undefined,
            }
          }))
          return
        }
        if (response.ok) {
          const doc = await response.json()
          const newFingerprint = contentFingerprint(doc.content)

          if (newFingerprint !== lastPolledFileFingerprintRef.current) {
            const timeSinceWebappWrite = lastWebappWriteTimeRef.current
              ? Date.now() - lastWebappWriteTimeRef.current
              : Infinity
            const isRecentWebappWrite = timeSinceWebappWrite < WEBAPP_WRITE_GRACE_MS

            lastPolledFileFingerprintRef.current = newFingerprint

            // Auto-save writes to disk then file watcher syncs — not an external edit.
            if (isRecentWebappWrite || localSavingRef.current) {
              return
            }

            if (!hasUnsavedChangesRef.current) {
              setHasUnsavedChanges(false)
              const split = bodyAndFrontmatterFromApiDoc(doc)
              setContent(split.body)
              setFrontmatter(split.frontmatter)
            } else if (doc.content !== contentRef.current) {
              const { toast } = await import('sonner')
              toast.info('File updated externally', {
                description: 'The file was modified. Click to refresh and see changes.',
                action: {
                  label: 'Refresh',
                  onClick: () => {
                    setHasUnsavedChanges(false)
                    const split = bodyAndFrontmatterFromApiDoc(doc)
                    setContent(split.body)
                    setFrontmatter(split.frontmatter)
                  }
                },
                duration: 5000
              })
            }
          }

          const incomingChanged = normalizeChangedLines(doc.changedLines ?? doc.changed_lines)
          setDocumentChangedLines(incomingChanged)
        }
      } catch (error) {
        console.warn('Polling failed:', error)
      }
    }

    const interval = setInterval(pollForUpdates, 3000)

    return () => clearInterval(interval)
  }, [documentId, documentTabActive])

  // Initial load + reload when document identity changes (must NOT live inside the polling effect above).
  useEffect(() => {
    let cancelled = false

    const loadDocument = async () => {
      if (!documentId || documentId === 'list' || documentId === 'home' || documentId.startsWith('new-')) {
        setLoading(false)
        return
      }

      if (isPathKeyedUnresolvedDocumentId(documentId)) {
        setLoading(false)
        return
      }

      if (documentId.startsWith('workspace-home-')) {
        setLoading(false)
        return
      }

      // Skip fetching for integration tabs (basecamp sections, projects, todos, etc.)
      if (
        documentId &&
        (isBasecampIntegrationTabId(documentId) ||
          documentId.startsWith('integration-') ||
          resolveBasecampTabFromShorthandPath(documentId) != null)
      ) {
        setLoading(false)
        return
      }

      // Soft reload: keep existing content visible (no skeleton flash) when re-fetching
      // the same document (tab focus / visibility). Fresh docs still show loading.
      const softReload =
        lastLoadedDocumentIdRef.current === documentId &&
        (contentRef.current?.length ?? 0) > 0
      if (!softReload) {
        setLoading(true)
      }
      setError(null)
      const fetchPath =
        documentId.includes('/')
          ? resolveStoragePathForFetch(documentId, workspacesRoute)
          : documentId
      const trimmedFallback = documentFilePathFallback?.trim() || null
      const resolvedFallback =
        trimmedFallback?.includes('/')
          ? resolveStoragePathForFetch(trimmedFallback, workspacesRoute)
          : trimmedFallback
      if (resolvedFallback?.includes('/') && !documentId.includes('/')) {
        fetchKeyPathRef.current = resolvedFallback
      } else if (documentId.includes('/')) {
        fetchKeyPathRef.current = fetchPath
      } else {
        fetchKeyPathRef.current = null
      }

      try {
        // Local dev: forceReadFromFile reads from disk (file watcher). Cloud has no FS - DB only.
        const forceFromFileByPath = IS_LOCAL_API ? '&forceReadFromFile=true' : ''
        const forceFromFileById = IS_LOCAL_API ? '?forceReadFromFile=true' : ''
        let apiUrl: string
        const pathKeyEarly = fetchKeyPathRef.current
        if (documentId.includes('/')) {
          apiUrl = `/api/documents/by-path?path=${encodeURIComponent(fetchPath)}${forceFromFileByPath}`
        } else if (pathKeyEarly && pathKeyEarly.includes('/')) {
          apiUrl = `/api/documents/by-path?path=${encodeURIComponent(pathKeyEarly)}${forceFromFileByPath}`
        } else {
          apiUrl = `/api/documents/${documentId}${forceFromFileById}`
        }

        let response = await fetchWithRetry(apiUrl, {
          retryOn404: !wasDocumentRecentlyDeleted(documentId, pathKeyEarly ?? resolvedFallback),
          max404Retries: wasDocumentRecentlyDeleted(documentId, pathKeyEarly ?? resolvedFallback) ? 0 : 2,
        })
        if (cancelled) return

        if (!response.ok && response.status === 404 && IS_LOCAL_API && apiUrl.includes('forceReadFromFile=true')) {
          let dbUrl: string
          if (documentId.includes('/')) {
            dbUrl = `/api/documents/by-path?path=${encodeURIComponent(fetchPath)}`
          } else if (pathKeyEarly && pathKeyEarly.includes('/')) {
            dbUrl = `/api/documents/by-path?path=${encodeURIComponent(pathKeyEarly)}`
          } else {
            dbUrl = `/api/documents/${documentId}`
          }
          response = await fetchWithRetry(dbUrl, { retryOn404: true, max404Retries: 2 })
          if (cancelled) return
        }

        const fallbackPath =
          documentFilePathFallback && documentFilePathFallback.trim().length > 0
            ? documentFilePathFallback.trim()
            : null
        const canTryPathFallback =
          Boolean(
            fallbackPath &&
              fallbackPath.includes('/') &&
              !documentId.includes('/') &&
              fallbackPath !== documentId
          )

        if (!response.ok && response.status === 404 && canTryPathFallback && fallbackPath && !apiUrl.includes('by-path')) {
          const byPathUrl = `/api/documents/by-path?path=${encodeURIComponent(fallbackPath)}${forceFromFileByPath}`
          response = await fetchWithRetry(byPathUrl, { retryOn404: true, max404Retries: 1 })
          if (cancelled) return
        }

        if (!response.ok) {
          if (cancelled) return
          // Mark as failed load to prevent redirect loops
          try {
            const { markFailedDocumentLoad } = await import('../../hooks/use-url-sync')
            markFailedDocumentLoad(documentId)
          } catch (err) {
            // Non-critical - just log
            console.debug('[DocumentContent] Could not mark failed load:', err)
          }
          throw new Error('Failed to load document')
        }

        const doc = await response.json()
        if (cancelled) return

        // Document loaded successfully - clear any failed load tracking
        try {
          const { clearFailedDocumentLoad } = await import('../../hooks/use-url-sync')
          clearFailedDocumentLoad?.(documentId)
          const resolvedId = doc.id
          if (resolvedId && resolvedId !== documentId) clearFailedDocumentLoad?.(resolvedId)
          const fp = (doc as { filePath?: string; file_path?: string }).filePath
            ?? (doc as { file_path?: string }).file_path
          if (typeof fp === 'string' && fp.length > 0) clearFailedDocumentLoad?.(fp)
        } catch (err) {
          // Non-critical
        }

        // Track document view (non-blocking, fire-and-forget)
        fetchWithAuth(`/api/documents/track-view`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            documentId: doc.id || documentId,
            viewSource: 'document-viewer',
            referrer: document.referrer || null
          })
        }).catch(err => {
          // Non-critical, silently fail
          console.debug('[DocumentContent] View tracking failed:', err)
        })

        const loadedRaw = doc.content || ''
        const split = bodyAndFrontmatterFromApiDoc({
          ...doc,
          content: loadedRaw,
        })
        const loadedContent = split.body
        // CRITICAL: Set hasUnsavedChanges to false BEFORE setting content
        // This prevents auto-save from triggering when loading document
        setHasUnsavedChanges(false)
        setContent(loadedContent)
        setSourceDraft(null)
        setFrontmatter(split.frontmatter)
        setLastSaved(doc.updated_at ? new Date(doc.updated_at) : null)
        setDocumentChangedLines(normalizeChangedLines(doc.changedLines ?? doc.changed_lines))

        // Seed poll fingerprint so the first poll doesn't see a phantom diff
        lastPolledFileFingerprintRef.current = contentFingerprint(loadedContent)
        initialLoadCompleteRef.current = true
        lastLoadedDocumentIdRef.current = documentId

        // Store document metadata for read-only check
        setCurrentDocument(doc)
        const loadedFp = (doc as { filePath?: string; file_path?: string }).filePath
          ?? (doc as { file_path?: string }).file_path
        if (typeof loadedFp === 'string' && loadedFp.includes('/')) {
          fetchKeyPathRef.current = loadedFp
        }

        // Check for localStorage backup and offer recovery if needed
        const backupKey = StorageKeys.BACKUP_DOCUMENT(documentId)
        try {
          const backup = localStorage.getItem(backupKey)
          if (backup) {
            const parsed = JSON.parse(backup)
            const backupTimestamp = parsed.timestamp
            const backupAge = Date.now() - backupTimestamp
            const maxAge = 5 * 60 * 1000 // 5 minutes

            // Get document's last updated timestamp (server must provide this to compare)
            const documentUpdatedAt = doc.updated_at ? new Date(doc.updated_at).getTime() : 0
            const hasValidServerTimestamp = documentUpdatedAt > 0

            // Check if backup content matches loaded content (already saved)
            const backupTrimmed = parsed.content?.trim() || ''
            const loadedTrimmed = loadedContent.trim()
            const contentMatches = backupTrimmed === loadedTrimmed

            // If backup content matches loaded content, it was already saved - clear it
            if (contentMatches) {
              localStorage.removeItem(backupKey)
            } else if (!hasValidServerTimestamp) {
              // No server timestamp (e.g. API flakiness, cached response) - don't show restore;
              // any backup would appear "newer" and cause false positives when opening via Browse
              localStorage.removeItem(backupKey)
            } else {
              // Only offer restore if:
              // 1. Backup is recent (within 5 minutes)
              // 2. Backup is NEWER than document's last update (not equal, and not stale)
              //    (If document was updated externally after backup, backup is stale)
              // 3. Content differs significantly
              const isBackupRecent = backupAge < maxAge
              const isBackupNewerThanDocument = backupTimestamp > documentUpdatedAt // Changed from >= to > (must be newer, not equal)
              const contentDiffers = parsed.content && parsed.content !== loadedContent

              if (isBackupRecent && isBackupNewerThanDocument && contentDiffers) {
                // Check if backup content is significantly different (more than just whitespace)
                if (backupTrimmed !== loadedTrimmed && backupTrimmed.length > 0) {
                  // Prevent multiple backup warnings - use a global flag
                  const backupWarningKey = `backup-warning-shown-${documentId}`
                  const alreadyShown = sessionStorage.getItem(backupWarningKey)

                  if (!alreadyShown) {
                    sessionStorage.setItem(backupWarningKey, 'true')

                    // Show recovery dialog after a short delay to let document load
                    setTimeout(() => {
                      import('sonner').then(({ toast }) => {
                        toast.warning('Unsaved changes detected', {
                          description: 'We found a backup of unsaved changes from Maater. Would you like to restore it?',
                          duration: 10000,
                          id: `backup-restore-${documentId}`, // Prevent duplicate toasts
                          action: {
                            label: 'Restore',
                            onClick: () => {
                              setContent(parsed.content)
                              setHasUnsavedChanges(true)
                              // Clear backup after restoring
                              localStorage.removeItem(backupKey)
                              sessionStorage.removeItem(backupWarningKey)
                              toast.dismiss(`backup-restore-${documentId}`)
                              toast.success('Backup restored', {
                                description: 'Your unsaved changes have been restored.',
                              })
                            },
                          },
                          cancel: {
                            label: 'Dismiss',
                            onClick: () => {
                              // Clear backup and flag if user dismisses
                              localStorage.removeItem(backupKey)
                              sessionStorage.removeItem(backupWarningKey)
                            },
                          },
                        })
                      })
                    }, 500)
                  }
                }
              } else if (backup && !isBackupNewerThanDocument) {
                // Backup is stale (document was updated externally after backup)
                // Silently clean it up
                localStorage.removeItem(backupKey)
              }
            }
          }
        } catch (err) {
          // Silently fail - backup recovery is optional
          console.warn('Failed to check backup:', err)
        }

        // Handle deleted documents
        if (doc.is_deleted) {
          setLocalIsDeleted(true)
          setDeletedMessage(doc.deleted_message || 'This document has been deleted. You can restore it using the button below.')
        } else {
          setLocalIsDeleted(false)
          setDeletedMessage(null)
        }
        } catch (err) {
        if (cancelled) return
        console.error('Failed to load document:', err)

        // Mark as failed load to prevent redirect loops
        try {
          const { markFailedDocumentLoad } = await import('../../hooks/use-url-sync')
          markFailedDocumentLoad(documentId)
        } catch (importErr) {
          console.debug('[DocumentContent] Could not mark failed load:', importErr)
        }

        // Inline error state only — no toast (delete flow already shows success + closes tab).
        // Skip console noise for intentional deletes and background tabs.
        const pathKey = fetchKeyPathRef.current ?? documentFilePathFallback
        const recentlyDeleted = wasDocumentRecentlyDeleted(documentId, pathKey)
        if (!recentlyDeleted && documentTabActive && !localIsDeleted) {
          console.debug('[DocumentContent] Document not found:', documentId)
        }

        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Document not found')
        }
      } finally {
        if (!cancelled) {
          setLoading(false)
        }
      }
    }

    loadDocument()
    // Reset edit-gate only when document identity changes (not on soft tab re-activate).
    if (prevDocumentIdForEditGateRef.current !== documentId) {
      prevDocumentIdForEditGateRef.current = documentId
      setWysiwygEditorMounted(false)
      setWysiwygAutoFocus(false)
    }
    return () => {
      cancelled = true
      setDocumentChangedLines(null)
    }
  }, [documentId, documentFilePathFallback, documentTabActive, normalizeChangedLines, workspacesRoute])
  useEffect(() => {
    if (loading) {
      navTimingReportedForLoadRef.current = null
      return
    }
    if (!documentTabActive) return
    if (
      !documentId ||
      documentId === "list" ||
      documentId === "home" ||
      documentId.startsWith("new-")
    ) {
      return
    }
    if (documentId.startsWith("workspace-home-")) return
    if (
      documentId === "basecamp" ||
      documentId === "basecamp-triage" ||
      documentId.startsWith("basecamp-project-") ||
      documentId.startsWith("basecamp-todo-") ||
      documentId.startsWith("integration-")
    ) {
      return
    }
    if (navTimingReportedForLoadRef.current === documentId) return
    navTimingReportedForLoadRef.current = documentId
    navDebug.markTabContentVisible(documentId)
  }, [loading, documentTabActive, documentId])

  // New documents: enter edit mode with caret ready once content has loaded.
  useEffect(() => {
    if (loading || !documentTabActive || viewMode !== "wysiwyg") return
    if (localIsDeleted || isDeleted) return
    if (
      !documentId ||
      documentId === "list" ||
      documentId === "home" ||
      documentId.startsWith("new-")
    ) {
      return
    }

    const docKeys = [
      documentId,
      currentDocument?.id,
      currentDocument?.path,
      currentDocument?.file_path,
      currentDocument?.filePath,
    ]
      .filter((key): key is string => typeof key === "string" && key.trim().length > 0)

    const shouldStartEditing = docKeys.some((key) => consumeDocumentEditOnOpen(key))
    if (!shouldStartEditing) return

    setViewMode("wysiwyg")
    setWysiwygAutoFocus(true)
    handleSetWysiwygEditorMounted(true)
  }, [
    loading,
    documentTabActive,
    viewMode,
    documentId,
    currentDocument?.id,
    currentDocument?.path,
    currentDocument?.file_path,
    currentDocument?.filePath,
    localIsDeleted,
    isDeleted,
    handleSetWysiwygEditorMounted,
    setViewMode,
  ])

  // Listen for document updates from AI edits
  useEffect(() => {
    const handleDocumentUpdate = (event: Event) => {
      if (!documentId) return

      const customEvent = event as CustomEvent
      const updatedDocId = customEvent.detail?.documentId
      const updatedPath = customEvent.detail?.filePath
      const incomingChangedLines = customEvent.detail?.changedLines

      devLog('[DocumentContent] Received document-updated event:', {
        updatedDocId,
        updatedPath,
        currentDocumentId: documentId
      })

      const isCurrentDoc = documentUpdateMatchesTab(
        documentId,
        updatedDocId,
        updatedPath,
        [
          documentFilePathFallback,
          fetchKeyPathRef.current,
          currentDocument?.id,
          currentDocument?.path,
          currentDocument?.file_path,
          currentDocument?.filePath,
        ]
      )

      if (isCurrentDoc) {
        // Chat Accept: clear gutters so source looks like the applied document, not a pending diff.
        if (
          incomingChangedLines === null ||
          (incomingChangedLines &&
            typeof incomingChangedLines === 'object' &&
            (incomingChangedLines as ChangedLines).change_status === 'accepted')
        ) {
          setDocumentChangedLines(null)
        } else if (incomingChangedLines !== undefined) {
          setDocumentChangedLines(normalizeChangedLines(incomingChangedLines))
        }

        // Don't interrupt active user editing - if the user has typed something (sourceDraft
        // is set) or has unsaved changes, skip the destructive remount. The change markers
        // above are still applied so the gutter stays in sync.
        if (hasUnsavedChangesRef.current || sourceDraftRef.current !== null) {
          devLog('[DocumentContent] Skipping refresh after document-updated - user has active edits')
          return
        }

        devLog('[DocumentContent] Reloading current document after AI edit')

        // Reuse the full external refresh path (remounts editor, updates WYSIWYG doc state)
        setTimeout(() => {
          window.dispatchEvent(new CustomEvent('document-refresh-requested', {
            detail: { documentId }
          }))
        }, 300)
      } else {
        devLog('[DocumentContent] Event for different document, ignoring')
      }
    }

    devLog('[DocumentContent] Added document-updated listener for:', documentId)
    window.addEventListener('document-updated', handleDocumentUpdate)
    return () => {
      devLog('[DocumentContent] Removed document-updated listener for:', documentId)
      window.removeEventListener('document-updated', handleDocumentUpdate)
    }
  }, [documentId, documentFilePathFallback, currentDocument, normalizeChangedLines])

  // Restore scroll position after explicit refresh remounts the editor
  useEffect(() => {
    if (pendingScrollRestoreRef.current == null) return
    const ratio = pendingScrollRestoreRef.current
    pendingScrollRestoreRef.current = null
    restoreDocumentScrollRatio(contentRootRef.current, ratio)
  }, [editorRefreshKey, viewMode, wysiwygEditorMounted])

  // Track if we're currently refreshing to prevent auto-save during refresh
  const isRefreshingRef = useRef(false)

  // Listen for webapp write events to track when files are written (from any source)
  useEffect(() => {
    if (!documentId) return

    const handleDocumentWrite = (event: Event) => {
      const customEvent = event as CustomEvent
      const writtenDocId = customEvent.detail?.documentId

      // If webapp wrote to this document, track the timestamp
      if (writtenDocId === documentId) {
        lastWebappWriteTimeRef.current = Date.now()
      }
    }

    window.addEventListener('document-written-by-webapp', handleDocumentWrite)
    return () => {
      window.removeEventListener('document-written-by-webapp', handleDocumentWrite)
    }
  }, [documentId])

  // Listen for external refresh requests (Phase 1: External file change detection)
  useEffect(() => {
    const handleRefreshRequest = (event: Event) => {
      const customEvent = event as CustomEvent
      const refreshDocId = customEvent.detail?.documentId

      // Only refresh if this is the current document
      if (refreshDocId !== documentId) return

      // Don't interrupt active user editing - a refresh would wipe sourceDraft and remount
      // the editor, losing any keystrokes the user has typed. The caller (document-updated
      // handler) already guards this, but document-refresh-requested can also arrive from
      // external file-change detection while the user is mid-edit.
      if (hasUnsavedChangesRef.current || sourceDraftRef.current !== null) {
        devLog('[DocumentContent] Skipping refresh-requested - user has active edits')
        return
      }

      devLog('[DocumentContent] External refresh requested for:', documentId)

      // Reload document from API
      // CRITICAL: Use forceReadFromFile=true to read directly from file (bypasses database)
      // No delay needed since we're reading from file directly, not waiting for file watcher sync
      const reload = async () => {
        if (!documentId || documentId === 'list' || documentId === 'home' || documentId.startsWith('new-')) return

        // Mark that we're refreshing to prevent auto-save
        isRefreshingRef.current = true

        try {
          const forceFromFileByPath = IS_LOCAL_API ? '&forceReadFromFile=true' : ''
          const forceFromFileById = IS_LOCAL_API ? '?forceReadFromFile=true' : ''
          let apiPath: string
          if (documentId.includes('/')) {
            const storagePath = resolveStoragePathForFetch(documentId, workspacesRoute)
            apiPath = `/api/documents/by-path?path=${encodeURIComponent(storagePath)}${forceFromFileByPath}`
          } else {
            apiPath = `/api/documents/${documentId}${forceFromFileById}`
          }
          const apiUrl = `${apiPath}`

          devLog('[DocumentContent] Fetching from:', apiUrl)
          devLog('[DocumentContent] URL includes forceReadFromFile:', apiUrl.includes('forceReadFromFile=true'))

          const response = await fetchWithAuth(apiUrl)
          if (response.ok) {
            const doc = await response.json()
            devLog('[DocumentContent] ✅ Received document - content length:', doc.content?.length)
            devLog('[DocumentContent] ✅ File modified at:', doc.file_modified_at)
            devLog('[DocumentContent] ✅ Response source flag:', doc._source)
            devLog('[DocumentContent] ✅ First 100 chars of content:', doc.content?.substring(0, 100))
            devLog('[DocumentContent] ✅ Full response keys:', Object.keys(doc))

            // Check if we got file content or database content
            if (!doc.file_modified_at && !doc._source) {
              console.warn('[DocumentContent] ⚠️ WARNING: Response missing file_modified_at and _source - likely from database, not file!')
            }

            // CRITICAL: Set hasUnsavedChanges to false BEFORE setting content
            // This prevents auto-save from triggering and overwriting the file
            setHasUnsavedChanges(false)

            // Use a small delay to ensure hasUnsavedChanges is set before content
            await new Promise(resolve => setTimeout(resolve, 50))

            // CRITICAL: Update content state - this will trigger editor to update via initialContent prop
            const newContent = doc.content || ''
            devLog('[DocumentContent] Updating content - old length:', content.length, 'new length:', newContent.length)
            devLog('[DocumentContent] Content changed?', content !== newContent)
            devLog('[DocumentContent] First 50 chars old:', content.substring(0, 50))
            devLog('[DocumentContent] First 50 chars new:', newContent.substring(0, 50))

            // Force update by setting content
            const split = bodyAndFrontmatterFromApiDoc({
              ...doc,
              content: newContent,
            })
            setContent(split.body)
            setSourceDraft(null)

            // Also update lastFileSave to track when file was last modified
            if (doc.file_modified_at) {
              setLastFileSave(new Date(doc.file_modified_at))
              // CRITICAL: Mark this as a webapp refresh so subsequent saves aren't blocked
              lastWebappWriteTimeRef.current = Date.now()
              // DON'T update fileModifiedAt here - it would remount on every poll; we use editorRefreshKey for explicit refresh only
            }
            // Force editor to remount so it shows the new content (MarkdownEditor only uses initialContent on mount)
            const scrollRatio = captureDocumentScrollRatio(contentRootRef.current)
            if (scrollRatio != null) {
              pendingScrollRestoreRef.current = scrollRatio
            }
            setEditorRefreshKey(prev => prev + 1)
            setFrontmatter(split.frontmatter)
            setCurrentDocument(doc)
            setLastSaved(doc.updated_at ? new Date(doc.updated_at) : null)
            setDocumentChangedLines(normalizeChangedLines(doc.changedLines ?? doc.changed_lines))
            devLog('[DocumentContent] ✅ Document refreshed from file (forceReadFromFile=true)')
          } else {
            const errorText = await response.text()
            console.error('[DocumentContent] Failed to refresh document:', response.status, errorText)
          }
        } catch (err) {
          console.error('[DocumentContent] Error refreshing document:', err)
        } finally {
          // Clear refresh flag after a longer delay to ensure all state updates complete
          // This prevents handleChange from triggering auto-save after refresh
          // Use longer delay to ensure editor has fully updated and won't trigger onChange
          setTimeout(() => {
            isRefreshingRef.current = false
            devLog('[DocumentContent] ✅ Refresh complete - auto-save re-enabled')
            devLog('[DocumentContent] ✅ Content after refresh:', content.substring(0, 100))
          }, 5000) // Increased to 5 seconds to ensure editor fully updates
        }
      }

      reload()
    }

    window.addEventListener('document-refresh-requested', handleRefreshRequest)
    return () => {
      window.removeEventListener('document-refresh-requested', handleRefreshRequest)
    }
  }, [documentId, normalizeChangedLines, workspacesRoute])

  // Cleanup timeout on unmount or document change
  useEffect(() => {
    return () => {
      if (saveTimeoutRef) {
        clearTimeout(saveTimeoutRef)
      }
    }
  }, [saveTimeoutRef, documentId])

  // Save on beforeunload (browser/tab close)
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges && documentId && !documentId.startsWith('new-')) {
        // Attempt to save using sendBeacon (reliable during unload)
        const contentToSave = content

        try {
          // Use FormData for sendBeacon (more reliable than Blob with PUT)
          const formData = new FormData()
          formData.append('content', contentToSave)
          formData.append('writeToFile', 'false')

          // Use POST endpoint for sendBeacon (PUT may not work)
          // Note: This requires a POST endpoint that accepts FormData
          // For now, fallback to localStorage backup
          const backupKey = StorageKeys.BACKUP_DOCUMENT(documentId)
          localStorage.setItem(backupKey, JSON.stringify({
            content: contentToSave,
            timestamp: Date.now(),
            documentId
          }))
        } catch (err) {
          console.warn('[Auto-save] Failed to save on unload:', err)
          // Store in localStorage as last resort
          const backupKey = StorageKeys.BACKUP_DOCUMENT(documentId)
          try {
            localStorage.setItem(backupKey, JSON.stringify({
              content: contentToSave,
              timestamp: Date.now(),
              documentId
            }))
          } catch (storageErr) {
            // localStorage might be full - that's okay, we tried
          }
        }

        // Show browser warning
        e.preventDefault()
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
        return e.returnValue
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => window.removeEventListener('beforeunload', handleBeforeUnload)
  }, [hasUnsavedChanges, documentId, content])

  // Save on visibility change (tab switch/app switch)
  // This will be updated after autoSave is defined
  const autoSaveRef = useRef<((contentToSave: string, options?: { immediate?: boolean }) => Promise<void>) | null>(null)

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden && hasUnsavedChanges && documentId && !documentId.startsWith('new-')) {
        // Tab is now hidden - save immediately
        if (saveTimeoutRef) {
          clearTimeout(saveTimeoutRef)
          setSaveTimeoutRef(null)
        }
        autoSaveRef.current?.(content, { immediate: true })
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [hasUnsavedChanges, documentId, content, saveTimeoutRef])

  // Cleanup old localStorage backups on mount
  useEffect(() => {
    const cleanupBackups = () => {
      const now = Date.now()
      const maxAge = 24 * 60 * 60 * 1000 // 24 hours

      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i)
        if (key?.startsWith(`${StorageKeys.BACKUP_DOCUMENT('').replace('_backup_', '_backup_')}`) || key?.includes('_backup_')) {
          try {
            const backup = localStorage.getItem(key)
            if (backup) {
              const parsed = JSON.parse(backup)
              if (parsed.timestamp && (now - parsed.timestamp) > maxAge) {
                localStorage.removeItem(key)
              }
            }
          } catch (err) {
            // Invalid backup, remove it
            localStorage.removeItem(key)
          }
        }
      }
    }

    cleanupBackups()
    // Run cleanup every hour
    const interval = setInterval(cleanupBackups, 60 * 60 * 1000)
    return () => clearInterval(interval)
  }, [])

  // Enhanced auto-save function with immediate option and reduced debounce
  const autoSave = useCallback(async (
    contentToSave: string,
    options: {
      immediate?: boolean
      frontmatterOverride?: Record<string, any> | null
    } = {}
  ) => {
    if (!documentId || documentId === 'list' || documentId === 'home' || documentId.startsWith('new-')) {
      return
    }

    // Never save empty/whitespace-only content over a document that had real content
    if (!contentToSave.trim() && currentDocument?.content?.trim()) {
      console.warn('[DocumentContent] Skipping auto-save - refusing to save empty content over non-empty document')
      return
    }

    // CRITICAL: Skip auto-save if we're currently refreshing (prevents overwriting file during refresh)
    if (isRefreshingRef.current) {
      devLog('[DocumentContent] Skipping auto-save - refresh in progress')
      return
    }

    // Skip auto-save if content + frontmatter haven't changed (hash comparison)
    const fmForHash =
      options.frontmatterOverride !== undefined
        ? options.frontmatterOverride
        : frontmatter
    const hashInput = `${contentToSave}\0${JSON.stringify(fmForHash ?? {})}`

    try {
      const encoder = new TextEncoder();
      const data = encoder.encode(hashInput);
      const hashBuffer = await crypto.subtle.digest('SHA-256', data);
      const hashArray = Array.from(new Uint8Array(hashBuffer));
      const currentHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');

      if (lastContentHashRef.current === currentHash && lastContentHashRef.current !== null) {
        devLog('[DocumentContent] Skipping auto-save - content unchanged (hash match)');
        if (hasUnsavedChanges) {
          setHasUnsavedChanges(false)
        }
        return;
      }
    } catch (hashError) {
      // Fallback: if crypto API not available, use simple string comparison
      console.warn('[DocumentContent] Crypto API not available, using string comparison fallback');
      if (lastContentHashRef.current === hashInput && lastContentHashRef.current !== null) {
        devLog('[DocumentContent] Skipping auto-save - content unchanged (string match)');
        if (hasUnsavedChanges) {
          setHasUnsavedChanges(false)
        }
        return;
      }
    }

    // Skip filesystem-only documents (read-only)
    if (currentDocument?.isReadOnly || currentDocument?.source === 'filesystem' || currentDocument?.id === null) {
      return
    }

    // CRITICAL: Check if file has been modified externally (prevents overwriting manual edits)
    // If file_modified_at is newer than our last save, skip auto-save to prevent overwriting
    // BUT: Allow save if file was modified by webapp recently (within grace period)
    if (currentDocument?.file_modified_at && lastFileSave) {
      const fileModifiedTime = new Date(currentDocument.file_modified_at).getTime();
      const lastSaveTime = lastFileSave.getTime();
      const now = Date.now();

      const timeSinceWebappWrite = lastWebappWriteTimeRef.current
        ? now - lastWebappWriteTimeRef.current
        : Infinity;
      const isRecentWebappWrite = timeSinceWebappWrite < WEBAPP_WRITE_GRACE_MS;

      // If file is newer than our last save AND it wasn't written by webapp recently, skip auto-save
      if (fileModifiedTime > lastSaveTime + 1000 && !isRecentWebappWrite) {
        console.warn('[DocumentContent] Skipping auto-save - file modified externally (file newer than last save)');
        console.warn(`[DocumentContent]   File mtime: ${currentDocument.file_modified_at}`);
        console.warn(`[DocumentContent]   Last save: ${lastFileSave.toISOString()}`);
        console.warn(`[DocumentContent]   Time since webapp write: ${timeSinceWebappWrite < Infinity ? Math.round(timeSinceWebappWrite / 1000) + 's' : 'never'}`);
        console.warn(`[DocumentContent]   This prevents overwriting manual file edits`);
        return;
      }
    }

    // Clear existing timeout
    if (saveTimeoutRef) {
      clearTimeout(saveTimeoutRef)
      setSaveTimeoutRef(null)
    }

    const performSave = async () => {
      // Double-check document still exists and is valid
      if (!documentId || documentId === 'list' || documentId === 'home' || documentId.startsWith('new-')) {
        return
      }

      // Skip auto-save for filesystem-only documents (read-only)
      if (currentDocument?.isReadOnly || currentDocument?.source === 'filesystem' || currentDocument?.id === null) {
        devLog('[DocumentContent] Skipping auto-save for filesystem-only document')
        return
      }

      setLocalSaving(true)
      setError(null)

      try {
        // CRITICAL: Fetch latest document state before saving to check for external changes
        // This prevents overwriting manual file edits with stale editor content
        const { getDocument } = await import('../../lib/api')
        let latestDoc: Document | null = null
        try {
          latestDoc = await getDocument(documentId)
        } catch (fetchError) {
          console.warn('[DocumentContent] Failed to fetch latest document state before auto-save:', fetchError)
          // Continue with save if fetch fails (fail open)
        }

        // If we got latest doc, check if file was modified externally
        // BUT: Allow save if file was modified by webapp recently (within grace period)
        if (latestDoc?.file_modified_at && lastFileSave) {
          const fileModifiedTime = new Date(latestDoc.file_modified_at).getTime();
          const lastSaveTime = lastFileSave.getTime();
          const now = Date.now();

          const timeSinceWebappWrite = lastWebappWriteTimeRef.current
            ? now - lastWebappWriteTimeRef.current
            : Infinity;
          const isRecentWebappWrite = timeSinceWebappWrite < WEBAPP_WRITE_GRACE_MS;

          // If file is newer than our last save AND it wasn't written by webapp recently, skip auto-save
          if (fileModifiedTime > lastSaveTime + 1000 && !isRecentWebappWrite) {
            console.warn('[DocumentContent] SKIPPING AUTO-SAVE - File modified externally after last save');
            console.warn(`[DocumentContent]   File mtime: ${latestDoc.file_modified_at}`);
            console.warn(`[DocumentContent]   Last save: ${lastFileSave.toISOString()}`);
            console.warn(`[DocumentContent]   Time since webapp write: ${timeSinceWebappWrite < Infinity ? Math.round(timeSinceWebappWrite / 1000) + 's' : 'never'}`);
            console.warn(`[DocumentContent]   This prevents overwriting manual file edits`);
            setLocalSaving(false)
            return
          }
        }

        const { updateDocument } = await import('../../lib/api')
        // Autosave: database + file write for two-way sync
        // Note: updateDocument will fail gracefully for filesystem-only docs
        // Clear change markers on auto-save if user has edited (accept external changes)
        const shouldClearMarkers = hasUnsavedChanges && documentChangedLines !== null
        const fmToSave =
          options.frontmatterOverride !== undefined
            ? options.frontmatterOverride
            : frontmatter
        // Only record a document_edits history row for "final" saves (leaving the
        // document via blur/tab-switch/tab-hide/document-switch), not for every
        // intermediate 1s-debounce autosave tick while the user is actively typing.
        // This keeps history entries meaningful without per-keystroke spam.
        // See docs/product/bets/2026-06-04-document-version-history-view.md (No-gos).
        const result = await updateDocument(documentId, {
          content: contentToSave,
          ...(fmToSave != null
            ? { frontmatter: fmToSave as Record<string, unknown> }
            : {}),
          writeToFile: true,
          clearChangeMarkers: shouldClearMarkers,
          saveEditHistory: options.immediate === true,
          changedBy: 'user',
          historyMetadata: { channel: 'webapp-editor', platform: 'webapp', saveTrigger: 'autosave' }
        })
        if (Object.prototype.hasOwnProperty.call(result, 'changedLines')) {
          setDocumentChangedLines(normalizeChangedLines(result.changedLines))
        } else if (result.document?.changed_lines) {
          setDocumentChangedLines(normalizeChangedLines(result.document.changed_lines))
        }

        // Clear baseline content when markers are cleared
        if (shouldClearMarkers) {
          setBaselineContent(null)
        }

        const now = new Date()
        setLastSaved(now)
        setLastFileSave(now) // Track file save separately
        setHasUnsavedChanges(false)

        // Track when webapp wrote to file (for auto-save protection logic)
        lastWebappWriteTimeRef.current = Date.now()

        // Dispatch event that external change detection can listen to
        // This tells the hook that webapp just wrote to file, so it shouldn't detect this as an external change
        if (result.document?.file_modified_at || result.document?.fileModifiedAt) {
          window.dispatchEvent(new CustomEvent('document-written-by-webapp', {
            detail: {
              documentId,
              fileModifiedAt: result.document.file_modified_at || result.document.fileModifiedAt
            }
          }))
        }

        // Update content hash after successful save (auto-save dedup)
        try {
          const encoder = new TextEncoder();
          const data = encoder.encode(hashInput);
          const hashBuffer = await crypto.subtle.digest('SHA-256', data);
          const hashArray = Array.from(new Uint8Array(hashBuffer));
          const currentHash = hashArray.map(b => b.toString(16).padStart(2, '0')).join('');
          lastContentHashRef.current = currentHash;
        } catch (hashError) {
          lastContentHashRef.current = hashInput;
        }

        // Sync poll fingerprint so the next poll doesn't see our own save as external
        const savedBody = result.document?.content ?? contentToSave
        lastPolledFileFingerprintRef.current = contentFingerprint(savedBody)

        // Clear localStorage backup on successful save
        // Also clear sessionStorage flag to prevent stale backup warnings
        const backupKey = StorageKeys.BACKUP_DOCUMENT(documentId)
        localStorage.removeItem(backupKey)
        const backupWarningKey = `backup-warning-shown-${documentId}`
        sessionStorage.removeItem(backupWarningKey)
      } catch (err) {
        console.error('Auto-save failed:', err)
        setError('Auto-save failed. Changes not saved.')

        // Show warning toast
        import('sonner').then(({ toast }) => {
          toast.error('Auto-save failed', {
            description: 'Your changes were not saved. A backup has been stored locally.',
            duration: 5000,
          })
        })

        // Store in localStorage as backup on failure
        const backupKey = StorageKeys.BACKUP_DOCUMENT(documentId)
        try {
          localStorage.setItem(backupKey, JSON.stringify({
            content: contentToSave,
            timestamp: Date.now(),
            documentId
          }))
        } catch (storageErr) {
          console.warn('Failed to store backup in localStorage:', storageErr)
        }
      } finally {
        setLocalSaving(false)
      }
    }

    if (options.immediate) {
      // Save immediately (blur, navigation, etc.)
      await performSave()
    } else {
      // Debounced save (normal typing) - reduced to 1 second
      const timeoutId = setTimeout(performSave, 1000)
      setSaveTimeoutRef(timeoutId)
    }
  }, [documentId, currentDocument, saveTimeoutRef, normalizeChangedLines, frontmatter, hasUnsavedChanges])

  // Update refs so other handlers can access autoSave
  useEffect(() => {
    autoSaveRef.current = autoSave
    autoSaveRefForSwitch.current = autoSave
  }, [autoSave])

  // Manual save function (database + file)
  const handleManualSave = async () => {
    if (!documentId || documentId === 'list' || documentId === 'home' || documentId.startsWith('new-')) {
      onManualSaveComplete?.()
      return
    }

    // Clear debounce timeout if exists
    if (saveTimeoutRef) {
      clearTimeout(saveTimeoutRef)
      setSaveTimeoutRef(null)
    }

    setError(null)

    setLocalSaving(true)
    try {
      let contentToSave = content
      if (viewMode === "wysiwyg" && wysiwygEditorMounted && wysiwygFlushRef.current) {
        contentToSave = wysiwygFlushRef.current()
      }

      const { updateDocument } = await import('../../lib/api')
      // Manual save: database + write to file
      // Clear change markers if user manually edited the document
      const shouldClearMarkers = hasUnsavedChanges && documentChangedLines !== null
      // Manual saves (Cmd+S / save button) are always deliberate "save" actions,
      // so always record a document_edits history row for version history.
      const result = await updateDocument(documentId, {
        content: contentToSave,
        ...(frontmatter != null
          ? { frontmatter: frontmatter as Record<string, unknown> }
          : {}),
        writeToFile: true,
        clearChangeMarkers: shouldClearMarkers,
        saveEditHistory: true,
        changedBy: 'user',
        historyMetadata: { channel: 'webapp-editor', platform: 'webapp', saveTrigger: 'manual' }
      })

      // Update changed lines state from response
      // If clearChangeMarkers was true, this will be null and clear the markers
      if (Object.prototype.hasOwnProperty.call(result, 'changedLines')) {
        setDocumentChangedLines(normalizeChangedLines(result.changedLines))
      } else if (result.document?.changed_lines !== undefined) {
        setDocumentChangedLines(normalizeChangedLines(result.document.changed_lines))
      }

      // Also clear baseline content when markers are cleared
      if (shouldClearMarkers) {
        setBaselineContent(null)
      }

      const now = new Date()
      setLastSaved(now)
      setLastFileSave(now) // Track file save separately
      setHasUnsavedChanges(false)

      // Track when webapp wrote to file (for auto-save protection logic)
      lastWebappWriteTimeRef.current = Date.now()

      const hashInputManual = `${contentToSave}\0${JSON.stringify(frontmatter ?? {})}`
      try {
        const encoder = new TextEncoder()
        const data = encoder.encode(hashInputManual)
        const hashBuffer = await crypto.subtle.digest('SHA-256', data)
        const hashArray = Array.from(new Uint8Array(hashBuffer))
        lastContentHashRef.current = hashArray.map(b => b.toString(16).padStart(2, '0')).join('')
      } catch {
        lastContentHashRef.current = hashInputManual
      }

      lastPolledFileFingerprintRef.current = contentFingerprint(
        result.document?.content ?? contentToSave
      )

      // Dispatch event that external change detection can listen to
      // This tells the hook that webapp just wrote to file, so it shouldn't detect this as an external change
      if (result.document?.file_modified_at || result.document?.fileModifiedAt) {
        window.dispatchEvent(new CustomEvent('document-written-by-webapp', {
          detail: {
            documentId,
            fileModifiedAt: result.document.file_modified_at || result.document.fileModifiedAt
          }
        }))
      }
      const { toast } = await import('sonner')
      toast.success('Saved', { id: 'doc-manual-save', duration: 1500 })
    } catch (err) {
      console.error('Manual save failed:', err)
      setError('Save failed. Please try again.')
      const { toast } = await import('sonner')
      toast.error('Save failed', { id: 'doc-manual-save' })
    } finally {
      setLocalSaving(false)
      onManualSaveComplete?.()
    }
  }

  // Listen for manual save trigger from parent
  useEffect(() => {
    if (onManualSave && saving) {
      handleManualSave()
    }
  }, [onManualSave, saving])

  // Cmd+S is registered at DocumentViewer (capture) so it works even when TipTap/CM has focus.
  // Keep a document-level fallback for standalone DocumentContent mounts (tests / embeds).
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's' && !e.shiftKey) {
        e.preventDefault()
        void handleManualSave()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [documentId, content, frontmatter, hasUnsavedChanges])

  const handleEditorChange = (newContent: string, options?: { immediate?: boolean; syncOnly?: boolean }) => {
    if (viewMode === "source") {
      setSourceDraft(newContent)
      const parsed = parseMarkdownWithFrontmatter(newContent)
      if (parsed.parseFailed) {
        import("sonner").then(({ toast }) => {
          toast.error("Invalid YAML in frontmatter", {
            id: "doc-fm-yaml",
            description: "Fix the YAML between the first pair of --- lines.",
          })
        })
        return
      }
      setContent(parsed.body)
      setFrontmatter(parsed.frontmatter as Record<string, any>)

      if (isRefreshingRef.current) {
        devLog(
          "[DocumentContent] Skipping handleEditorChange auto-save - refresh in progress"
        )
        return
      }

      setHasUnsavedChanges(true)
      autoSave(parsed.body, {
        ...options,
        frontmatterOverride: parsed.frontmatter as Record<string, any>,
      })

      if (documentId && !documentId.startsWith("new-")) {
        const backupKey = StorageKeys.BACKUP_DOCUMENT(documentId)
        try {
          const contentSize = new Blob([newContent]).size
          if (contentSize < 1024 * 1024) {
            localStorage.setItem(
              backupKey,
              JSON.stringify({
                content: parsed.body,
                timestamp: Date.now(),
                documentId,
              })
            )
          }
        } catch (err) {
          console.warn("Failed to backup to localStorage:", err)
        }
      }
      return
    }

    if (sourceDraft !== null) setSourceDraft(null)

    setContent(newContent)

    if (options?.syncOnly) {
      return
    }

    if (isRefreshingRef.current) {
      devLog(
        "[DocumentContent] Skipping handleEditorChange auto-save - refresh in progress"
      )
      return
    }

    setHasUnsavedChanges(true)
    autoSave(newContent, options)

    if (documentId && !documentId.startsWith("new-")) {
      const backupKey = StorageKeys.BACKUP_DOCUMENT(documentId)
      try {
        const contentSize = new Blob([newContent]).size
        if (contentSize < 1024 * 1024) {
          localStorage.setItem(
            backupKey,
            JSON.stringify({
              content: newContent,
              timestamp: Date.now(),
              documentId,
            })
          )
        }
      } catch (err) {
        console.warn("Failed to backup to localStorage:", err)
      }
    }
  }

  const handleRestore = async () => {
    if (!documentId || documentId === 'list' || documentId === 'home' || documentId.startsWith('new-')) {
      return
    }

    try {
      const { restoreDocument } = await import('../../lib/api')
      await restoreDocument(documentId)

      // Reload the document
      setLocalIsDeleted(false)
      setDeletedMessage(null)

      // Trigger a reload by updating the document ID
      window.location.reload()
    } catch (err) {
      console.error('Failed to restore document:', err)
      setError('Failed to restore document. Please try again.')
    }
  }

  // Show loading state
  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary mb-2" />
          <p className="text-sm text-muted-foreground">Loading document...</p>
        </div>
      </div>
    )
  }

  const pathFromDoc = String(
    currentDocument?.filePath || currentDocument?.file_path || ""
  )
  const isHtmlDocument =
    Boolean(currentDocument) &&
    (currentDocument?.type === "html" ||
      pathFromDoc.toLowerCase().endsWith(".html") ||
      (typeof documentId === "string" && documentId.toLowerCase().endsWith(".html")))

  const isCsvDocumentView =
    Boolean(currentDocument) &&
    isCsvDocument({
      type: currentDocument?.type,
      filePath: pathFromDoc || (typeof documentId === "string" ? documentId : null),
    })

  if (isCsvDocumentView) {
    return (
      <div className="relative flex h-full flex-col">
        <div className="min-h-0 flex-1 overflow-hidden">
          <CsvDocumentViewer
            content={content}
            documentTitle={frontmatter?.title || currentDocument?.title}
            readOnly={isDeleted}
            onChange={(next) => {
              setContent(next)
              setHasUnsavedChanges(true)
              autoSave(next)
            }}
          />
        </div>
        <DocumentContentStatusBar
          viewMode="source"
          wysiwygEditorMounted={false}
          localIsDeleted={localIsDeleted}
          pageWidthMode={pageWidthMode}
          showPageWidthToggle={false}
          showEditToggle={false}
          onSetViewMode={() => {}}
          onSetWysiwygEditorMounted={() => {}}
          onTogglePageWidthMode={() => {}}
          localSaving={localSaving}
          hasUnsavedChanges={hasUnsavedChanges}
          lastSaved={lastSaved}
          lastFileSave={lastFileSave}
          deletedMessage={deletedMessage}
          onRestore={handleRestore}
          contentLineCount={content.split("\n").length}
          contentCharCount={content.length}
          error={_error}
          driveSyncActivity={driveSyncActivity}
          driveSyncLabel={driveSyncLabel}
          driveGdocId={driveGdocId}
          driveOrganizationId={driveOrganizationId}
          driveLastSynced={driveLastSynced}
          driveLastPushed={driveLastPushed}
          driveLastPulled={driveLastPulled}
          driveKeepSynced={driveKeepSynced}
        />
      </div>
    )
  }

  if (isHtmlDocument) {
    return (
      <DocumentContentHtmlPanel
        content={content}
        documentTitle={frontmatter?.title || currentDocument?.title}
      />
    )
  }

  return (
    <div className="h-full flex flex-col relative">
      {/* Visual mode banner - absolutely positioned to avoid layout shift */}
      {changeBannerVisible && documentChangedLines && (
        <ChangeDetectionBanner
          additions={documentChangedLines.added?.length || 0}
          deletions={documentChangedLines.removed?.length || 0}
          onViewInSource={() => setViewMode('source')}
          onDismiss={async () => {
            if (!documentId) {
              setDocumentChangedLines(null)
              return
            }
            try {
              const { updateDocument } = await import('../../lib/api')
              const result = await updateDocument(documentId, {
                clearChangeMarkers: true,
                writeToFile: false,
              })
              if (Object.prototype.hasOwnProperty.call(result, 'changedLines')) {
                setDocumentChangedLines(normalizeChangedLines(result.changedLines))
              } else {
                setDocumentChangedLines(null)
              }
              setBaselineContent(null)
            } catch (err) {
              console.error('[DocumentContent] Failed to dismiss change markers:', err)
              setDocumentChangedLines(null)
              setBaselineContent(null)
            }
          }}
        />
      )}

      {/* Editor - add padding-top when banner is visible */}
      <div
        ref={contentRootRef}
        className={`flex-1 flex flex-col overflow-hidden min-h-0 transition-all duration-200 ${
          changeBannerVisible ? 'pt-12' : ''
        } ${
          isEditorFocused
            ? 'ring-2 ring-primary/20 ring-inset shadow-[inset_0_0_8px_hsl(var(--primary)/0.15)]'
            : ''
        }`}
        onFocus={() => setIsEditorFocused(true)}
        onBlur={(e) => {
          // Only blur if focus is moving outside the editor container
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsEditorFocused(false)
          }
        }}
      >
        {viewMode === 'source' && (
            <DocumentContentSourceFrontmatterBar
              showFrontmatterInSource={showFrontmatterInSource}
              onToggle={(next) => {
                setShowFrontmatterInSource(next)
                setSourceFrontmatterPrefs((prev) => ({
                  ...prev,
                  [structuralKind]: next,
                }))
              }}
            />
          )}
        {viewMode === "wysiwyg" && CustomRenderer && currentDocument ? (
          <div className="min-h-0 flex-1 overflow-hidden">
            <CustomRenderer
              document={{
                ...(currentDocument as Document),
                frontmatter: frontmatter || currentDocument.frontmatter,
                filePath:
                  (currentDocument as Document).filePath ||
                  (currentDocument as { file_path?: string }).file_path,
              }}
              sourceViewActive={false}
            />
          </div>
        ) : (
          <div className="min-h-0 flex-1 overflow-hidden">
            <MarkdownEditor
              key={`${fileModifiedAt || documentId}-${editorRefreshKey}`} // Remount when file or explicit refresh changes so new content is shown
              initialContent={markdownInitialContent}
              onChange={handleEditorChange}
              darkMode={resolvedTheme === 'dark'}
              className="h-full"
              readOnly={isDeleted}
              onInternalLinkClick={onInternalLinkClick}
              viewMode={viewMode}
              changedLines={editorChangedLines}
              showChangeMarkers={showChangeMarkers && viewMode === 'source'}
              documentId={documentId}
              documentTitle={frontmatter?.title || currentDocument?.title}
              documentPath={currentDocument?.path || currentDocument?.file_path}
              documentType={currentDocument?.type ?? null}
              documentFrontmatter={frontmatter}
              onFocus={() => setIsEditorFocused(true)}
              onBlur={() => setIsEditorFocused(false)}
              collapseFrontmatter={viewMode === "source" && !showFrontmatterInSource}
              wysiwygEditorMounted={wysiwygEditorMounted}
              onActivateWysiwyg={() => handleSetWysiwygEditorMounted(true)}
              wysiwygAutoFocus={wysiwygAutoFocus}
              onWysiwygAutoFocusComplete={() => setWysiwygAutoFocus(false)}
              pageWidthMode={pageWidthMode}
              wysiwygFlushRef={wysiwygFlushRef}
            />
          </div>
        )}
      </div>

      {CustomRenderer && structuralKind === "flow" ? null : (
      <DocumentContentStatusBar
        viewMode={viewMode}
        wysiwygEditorMounted={wysiwygEditorMounted}
        localIsDeleted={localIsDeleted}
        pageWidthMode={pageWidthMode}
        showPageWidthToggle={!CustomRenderer}
        showEditToggle={!CustomRenderer}
        onSetViewMode={setViewMode}
        onSetWysiwygEditorMounted={handleSetWysiwygEditorMounted}
        onTogglePageWidthMode={() =>
          setPageWidthMode((m) => (m === "default" ? "full" : "default"))
        }
        localSaving={localSaving}
        hasUnsavedChanges={hasUnsavedChanges}
        lastSaved={lastSaved}
        lastFileSave={lastFileSave}
        deletedMessage={deletedMessage}
        onRestore={handleRestore}
        contentLineCount={content.split("\n").length}
        contentCharCount={content.length}
        error={_error}
        driveSyncActivity={driveSyncActivity}
        driveSyncLabel={driveSyncLabel}
        driveGdocId={driveGdocId}
        driveOrganizationId={driveOrganizationId}
        driveLastSynced={driveLastSynced}
        driveLastPushed={driveLastPushed}
        driveLastPulled={driveLastPulled}
        driveKeepSynced={driveKeepSynced}
        infoTips={
          structuralKind === "product-board" ? LIST_GESTURE_TIPS_PRODUCT_BOARD : undefined
        }
      />
      )}
    </div>
  )
}

/** Memoized to avoid re-renders when switching tabs - only active and previously-active tabs need updates */
export const DocumentContent = memo(DocumentContentInner)
